import React from 'react';

import {
    Anchor,
    Box,
    Collapsible,
    Divider,
    Flex,
    Heading,
    IconChevronDown,
    IconChevronUp,
    List,
    Paragraph,
    TextButton,
} from '@zillow/constellation';
import { useBotState, useMobileState, useTabletState } from '@zillow/shopper-platform-config';
import { yieldCallback } from '@zillow/yield-callback';

import { WRAPPER_SX } from '../../constants/styles';
import { useGlobalStoreState } from '../../contexts/GlobalStoreProvider';
import { Container } from '../Container/Container';
import HomeRecsFooter from '../HomeRecsFooter/HomeRecsFooter';
import type { LinkConfig } from './constants';
import { LINKS_BROWSE_HOMES, LINKS_MORTGAGE, LINKS_REAL_ESTATE, LINKS_RENTALS } from './constants';

const FooterContainerList: React.FC<{ title: string; links: LinkConfig[] }> = ({
    title,
    links,
}) => {
    const visibileDividerSx = {
        visibility: 'visible',
        width: '1px',
        margin: '$spacing.none $spacing.sm',
    } as const;

    const hiddenDividerSx = {
        visibility: 'hidden',
        width: '$spacing.none',
        margin: '$spacing.none',
    } as const;

    return (
        <Box
            as={List.Item}
            sx={{
                // dividers
                '[role="separator"]': {
                    height: '$spacing.md',
                    ...visibileDividerSx,
                },
                // hide 'even' dividers when NOT in $lg
                '&:nth-child(even)': {
                    '[role="separator"]': hiddenDividerSx,
                },
                // hide ALL dividers when < xs
                [`@media $xs_lte`]: {
                    '[role="separator"]': hiddenDividerSx,
                },
                '@media $lg': {
                    '[role="separator"]': {
                        height: '$spacing.lg',
                    },
                    // show 'even' dividers when IN $lg
                    '&:nth-child(even)': {
                        '[role="separator"]': visibileDividerSx,
                    },
                    // except for the last one
                    '&:last-of-type': {
                        '[role="separator"]': hiddenDividerSx,
                    },
                },
            }}
        >
            <Collapsible
                defaultCollapsed
                // eslint-disable-next-line zillow/react/no-children-prop
                children={(collapsed, toggle) => (
                    <React.Fragment>
                        <Flex
                            display="flex"
                            alignItems="center"
                            justifyContent={{ default: 'space-between', lg: 'space-around' }}
                        >
                            <Box
                                as={TextButton}
                                fontType="body"
                                buttonType="secondary"
                                icon={
                                    collapsed ? (
                                        <IconChevronDown fontColor="brand" />
                                    ) : (
                                        <IconChevronUp fontColor="brand" />
                                    )
                                }
                                iconSize="xs"
                                iconPosition="end"
                                sx={{
                                    'display': 'flex !important',
                                    'justifyContent': 'space-between !important',
                                    'width': '100%',
                                    '@media $lg': {
                                        justifyContent: 'center !important',
                                    },
                                }}
                                onClick={yieldCallback(toggle)}
                            >
                                {title}
                            </Box>
                            <Divider orientation="vertical" />
                        </Flex>
                        <Box
                            as={List}
                            appearance="none"
                            sx={{ display: collapsed ? 'none' : 'block' }}
                        >
                            {links.map(({ href, text }) => (
                                <Box
                                    as={List.Item}
                                    key={href}
                                    marginX={undefined}
                                    sx={{
                                        '@media $lg': {
                                            textAlign: 'center',
                                            // Center by substracting divider Xmargin (*2)
                                            marginRight: '$spacing.lg !important',
                                        },
                                    }}
                                >
                                    <Paragraph fontType="bodySmall">
                                        <Anchor href={href}>{text}</Anchor>
                                    </Paragraph>
                                </Box>
                            ))}
                        </Box>
                    </React.Fragment>
                )}
            />
        </Box>
    );
};
const RegionFooterLinks: React.FC = () => {
    const { showNYFairHousingMsg } = useGlobalStoreState();
    const isBot = useBotState();

    const isMobile = useMobileState();
    const isTablet = useTabletState();
    const isMobileOrTablet = isMobile || isTablet;

    let fairHousingNYPDF: string;
    fairHousingNYPDF = 'https://dos.ny.gov/system/files/documents/2021/08/fairhousingnotice.pdf';
    if (isMobileOrTablet) {
        fairHousingNYPDF = `https://docs.google.com/gview?embedded=true&url=${fairHousingNYPDF}`;
    }

    return (
        <Box
            sx={{
                ...WRAPPER_SX,
                paddingTop: '$spacing.sm',
                paddingBottom: '$spacing.md',
            }}
        >
            <Container gap="lg">
                <HomeRecsFooter />
                {(showNYFairHousingMsg || isBot) && (
                    <Box
                        as={Flex}
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        gap={0}
                    >
                        <Heading level="6">You have a right to fair housing.</Heading>
                        <Paragraph fontType="body">
                            <Anchor
                                target={isMobileOrTablet ? '_blank' : '_self'}
                                href={fairHousingNYPDF}
                            >
                                Learn about New York Fair Housing protections
                            </Anchor>
                        </Paragraph>
                    </Box>
                )}
                <Box
                    as={List}
                    appearance="none"
                    sx={{
                        'display': 'grid',
                        'width': '100%',
                        'gridTemplateColumns': 'repeat(2, 1fr)',
                        '@media $lg': {
                            gridTemplateColumns: 'repeat(4, 1fr)',
                        },
                        [`@media $xs_lte`]: {
                            gridTemplateColumns: '1fr',
                        },
                    }}
                >
                    <FooterContainerList title="Real Estate" links={LINKS_REAL_ESTATE} />
                    <FooterContainerList title="Rentals" links={LINKS_RENTALS} />
                    <FooterContainerList title="Mortgage Rates" links={LINKS_MORTGAGE} />
                    <FooterContainerList title="Browse Homes" links={LINKS_BROWSE_HOMES} />
                </Box>
            </Container>
        </Box>
    );
};

export default RegionFooterLinks;
